import { api } from '~/data/api'

/**
 * Interface representing a feature flag result
 * @interface IZehEnabledResult
 */
export interface IZehEnabledResult {
  result: boolean
}

export async function getIsZehEnabled(): Promise<IZehEnabledResult> {
  try {
    const response = await api().get('/zeh/enabled')

    return response.data
  } catch (error) {
    console.error('Error while checking Zeh availability', error)
    throw error
  }
}

/**
 * Interface representing a conversation with the AI assistant
 * @interface IConversation
 */
export interface IConversation {
  _id?: string
  session_id: string
  status: 'active' | 'error' | 'completed'
  context: Array<{
    message: string
    speaker: 'human' | 'assistant'
    processing_time_seconds?: number
  }>
  last_interaction_context?: {
    message: string
    speaker: 'human' | 'assistant'
    processing_time_seconds?: number
  }
  created_at?: string
  updated_at?: string
}

export interface IConversationStatusResponse {
  session_id: string
  status: 'active' | 'completed' | 'error' | 'idle'
  response?: string
  error?: string
}

export interface ISendMessageParams {
  session_id: string
  user_input: string
}

/**
 * Creates a new conversation with the AI assistant
 * @param userInput - The initial user message
 * @returns Promise with the created conversation
 */
export async function createConversation(userInput: string): Promise<IConversation> {
  try {
    const response = await api().post('/zeh/conversations', {
      user_input: userInput,
    })
    return response.data
  } catch (error) {
    console.error('Error creating conversation:', error)
    throw error
  }
}

/**
 * Lists all conversations for the current user
 * @returns Promise with an array of conversations
 */
export async function listConversations(): Promise<IConversation[]> {
  try {
    const response = await api().get('/zeh/conversations')
    return response.data
  } catch (error) {
    console.error('Error listing conversations:', error)
    throw error
  }
}

/**
 * Gets a specific conversation by ID
 * @param id - The conversation session ID
 * @returns Promise with the conversation data
 */
export async function getConversation(id: string): Promise<IConversation> {
  try {
    const response = await api().get(`/zeh/conversations/${id}`)
    return response.data
  } catch (error) {
    console.error(`Error getting conversation ${id}:`, error)
    throw error
  }
}

/**
 * Sends a message to the conversation
 * @param params - The send message parameters
 * @returns Promise with the send response
 */
export async function sendMessage(params: ISendMessageParams): Promise<{ status: string }> {
  const response = await api().post('/zeh/conversations/message', {
    session_id: params.session_id,
    user_input: params.user_input,
  })
  return response.data
}

type AverageInspectorsData = {
  inspectors: {
    [key: string]: {
      sessions: Record<string, string>[]
    }
  }
}

export interface IDetailedSessions {
  date: string
  lead_time: number
  lag_time: number
  net_time: number
}

export interface ILogTimeInspector {
  inspector: string
  profile: string
  total_time: number
  total_inspections: number
}

export interface ILogPeriodTimeInspector {
  [period: string]: ILogTimeInspector
}

export interface IAuditByInspector {
  inspection_id: string
  aircraft_asset_type: string
  creation_date: string
  date_of_audit: string
  started_date: string
  due_date: string
  inspection_number: string
  aircraft: string
  status: string
  country: string
  operator: string
  serial_number: string
  model: string
  manufacture: string
  scope: string
  inspector_id: string
  inspector_name: string
  inspector_email: string
  inspector_company: string
  inspector_profile: string
  audit_lead_time: string
  inspector_net_lead_time: string
  audit_net_lead_time: string
  audit_lag_time: string
  inspector_lag_time: string
  finding_answer_lead_time: string
  inspector_finding_answer_lead_time: string
  audit_finding_answer_sessions: Record<string, string>[]
  inspector_finding_answer_sessions: Record<string, string>[]
  closed_date: string
  time_to_close: string
  inspector_sessions: []
  inspector_sessions_detailed: IDetailedSessions[]
}

export interface IStaticDiscrepancy {
  operator: string
  aircraft_model: string
  aircraft_manufacturer: string
  type: string
  original_type: string
  classification: string
  category: string
  location: string
  item_type: string
  status: string
  report_photo_section: string
  report_photo_category: string
}

export interface ITypeClassifications {
  main_category_stats: {
    [category: string]: {
      count: number
      percentage: number
    }
  }
  status: string
  status_stats: {
    [status: string]: {
      count: number
      percentage: number
    }
  }
  summary: {
    most_common_main_category: string
    most_common_status: string
    total_main_categories: number
  }
  total_classifications: number
  total_discrepancies: number
}

export interface IUnifiedStatsResponse {
  // Legacy chart data URLs
  audit_by_inspector?: string
  report_lead_time?: string
  average_inspectors?: string
  discrepancy?: string
  timeline?: string

  // New unified data
  type_classifications: ITypeClassifications
  findings_by_type: IFindingsByType
  findings_by_severity: IFindingsBySeverity
  findings_by_location: IFindingsByLocation
}

export interface IFindingsData {
  status: string
  total_findings: number
  total_classifications?: number
  filters_applied: {
    manufacturer?: string
    model?: string
    operator?: string
    country?: string
    region?: string
    start_date?: string
    end_date?: string
  }
}

export interface IFindingsByType extends IFindingsData {
  type_distribution: {
    [type: string]: {
      count: number
      percentage: number
    }
  }
}

export interface IFindingsBySeverity extends IFindingsData {
  severity_distribution: {
    [severity: string]: {
      count: number
      percentage: number
    }
  }
}

export interface IFindingsByLocation extends IFindingsData {
  category_distribution: {
    [location: string]: {
      count: number
      percentage:
        | number
        | {
            source: string
            parsedValue: number
          }
    }
  }
}

export interface IFindingsFilters {
  manufacturer?: string // Manufacturer ID
  model?: string // Model ID
  operator?: string // Operator/Company ID
  country?: string
  region?: string
  start_date?: string
  end_date?: string
}

export interface ITechnicalByCategory extends IFindingsData {
  category_distribution: {
    [category: string]: {
      count: number
      percentage: number
    }
  }
  summary: {
    total_categories: number
    most_common_category: string | null
  }
}

export interface IAuditPerQuarterByStatus {
  quarter: string
  status: string
  count: number
  filter_type: 'all_auditors' | 'by_auditor' | 'by_operator' | 'by_auditor_and_operator'
  inspector_id: string | null
  inspector_name: string | null
  operator_id: string | null
  operator_name: string | null
}

export interface IUserSession {
  user_id: string
  name: string
  sessions: Record<string, number>[]
}

export interface IReportLeadTime {
  inspector_name: string
  operator_name: string
  quarter: string
  quarter_log: string
  lead_time: number
  inspection_id: string
}

export interface ILeadTimeBreakdown {
  inspection_id: string
  inspection_number: string
  inspector: string
  worked_days: string[]
  operator: string
  create_to_start: number
  start_to_close: number
}

interface IQuarterTimeline {
  audits_lead_time: number
  audits_net_lead_time: number
  audits_lag_time: number
  quarter: string
}

export interface ITimeline {
  inspection_id: string
  operator: string
  inspector_id: string
  inspector_name: string
  quarters: IQuarterTimeline[]
}

interface IChartsDataResponse {
  lead_time_breakdown: ILeadTimeBreakdown[] | null
  report_lead_time: IReportLeadTime[] | null
  audit_by_inspector?: IAuditByInspector[] | null
  average_inspectors?: AverageInspectorsData | null
  discrepancy?: IStaticDiscrepancy[] | null
  timeline?: ITimeline[] | null
  log_time_inspector?: ILogPeriodTimeInspector | null
  audits_per_quarter_by_status?: IAuditPerQuarterByStatus[] | null
}

export async function getChartsData(): Promise<IChartsDataResponse> {
  const response = await api().get('/zeh/stats')
  const {
    audit_by_inspector,
    average_inspectors,
    discrepancy,
    timeline,
    report_lead_time,
    log_time_inspector,
    lead_time_breakdown,
    audits_per_quarter_by_status,
  } = response.data

  const urlMap: Record<string, string> = {
    audit_by_inspector,
    average_inspectors,
    discrepancy,
    timeline,
    report_lead_time,
    log_time_inspector,
    lead_time_breakdown,
    audits_per_quarter_by_status,
  }

  try {
    const entries = Object.entries(urlMap)
    const results = await Promise.all(
      entries.map(async ([key, url]) => {
        if (!url) return [key, null]
        const response = await fetch(url)
        const data = await response.json()
        return [key, data]
      }),
    )

    return Object.fromEntries(results)
  } catch (error) {
    console.error('Error fetching chart data:', error)
    return {
      audit_by_inspector: null,
      average_inspectors: null,
      discrepancy: null,
      timeline: null,
      report_lead_time: null,
      log_time_inspector: null,
      lead_time_breakdown: null,
      audits_per_quarter_by_status: null,
    }
  }
}

export async function getBasicStats(): Promise<
  Omit<IUnifiedStatsResponse, 'findings_by_type' | 'findings_by_severity' | 'findings_by_location'>
> {
  try {
    const response = await api().get('/zeh/stats')
    return response.data
  } catch (error) {
    console.error('Error fetching basic stats:', error)
    throw error
  }
}

export async function getFindingsStats(filters?: IFindingsFilters): Promise<{
  findings_by_type: IFindingsByType
  findings_by_severity: IFindingsBySeverity
  findings_by_location: IFindingsByLocation
}> {
  try {
    const params = new URLSearchParams()
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value)
      })
    }

    const response = await api().get(`/zeh/stats/findings?${params.toString()}`)
    return {
      findings_by_type: response.data.findings_by_type,
      findings_by_severity: response.data.findings_by_severity,
      findings_by_location: response.data.findings_by_location_classification,
    }
  } catch (error) {
    console.error('Error fetching findings stats:', error)
    throw error
  }
}

export async function getUnifiedStats(filters?: IFindingsFilters): Promise<IUnifiedStatsResponse> {
  try {
    // Make parallel requests to both endpoints
    const [basicStatsResponse, findingsStatsResponse] = await Promise.all([
      getBasicStats(),
      getFindingsStats(filters),
    ])

    // Combine the responses
    return {
      ...basicStatsResponse,
      ...findingsStatsResponse,
    }
  } catch (error) {
    console.error('Error fetching unified stats:', error)
    throw error
  }
}

// Keep these for backward compatibility but they now use the unified call
export async function getTypeClassifications(): Promise<ITypeClassifications> {
  try {
    const unifiedStats = await getUnifiedStats()
    return unifiedStats.type_classifications
  } catch (error) {
    console.error('Error fetching type classifications:', error)
    throw error
  }
}

export async function getFindingsByType(filters?: IFindingsFilters): Promise<IFindingsByType> {
  try {
    const params = new URLSearchParams()
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value)
      })
    }

    const response = await api().get(`/zeh/stats/findings?${params.toString()}`)
    return response.data.findings_by_type
  } catch (error) {
    console.error('Error fetching findings by type:', error)
    throw error
  }
}

export async function getFindingsBySeverity(
  filters?: IFindingsFilters,
): Promise<IFindingsBySeverity> {
  try {
    const params = new URLSearchParams()
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value)
      })
    }

    const response = await api().get(`/zeh/stats/findings?${params.toString()}`)
    return response.data.findings_by_severity
  } catch (error) {
    console.error('Error fetching findings by severity:', error)
    throw error
  }
}

export async function getFindingsByLocation(
  filters?: IFindingsFilters,
): Promise<IFindingsByLocation> {
  try {
    const params = new URLSearchParams()
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value)
      })
    }

    const response = await api().get(`/zeh/stats/findings?${params.toString()}`)
    return response.data.findings_by_location_classification
  } catch (error) {
    console.error('Error fetching findings by location:', error)
    throw error
  }
}

export async function getTechnicalStats(filters?: IFindingsFilters): Promise<ITechnicalByCategory> {
  try {
    const params = new URLSearchParams()
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value)
      })
    }

    const response = await api().get(`/zeh/stats/technical?${params.toString()}`)
    const data = response.data

    if (data && data.category_distribution) {
      const newDistribution: { [category: string]: { count: number; percentage: number } } = {}
      const prefixRegex = /^\d+\.\d+\.?\s*(?:[-–]\s*)?/

      Object.entries(data.category_distribution).forEach(
        ([key, value]: [string, { count: number; percentage: number }]) => {
          const normalizedKey = key.replace(prefixRegex, '').replace(/–/g, '-').trim()

          if (newDistribution[normalizedKey]) {
            newDistribution[normalizedKey].count += value.count
            newDistribution[normalizedKey].percentage += value.percentage
            newDistribution[normalizedKey].percentage = parseFloat(
              newDistribution[normalizedKey].percentage.toFixed(1),
            )
          } else {
            newDistribution[normalizedKey] = { ...value }
          }
        },
      )

      data.category_distribution = newDistribution
      data.summary.total_categories = Object.keys(newDistribution).length

      let maxCount = -1
      let mostCommon = null

      Object.entries(newDistribution).forEach(([key, value]) => {
        if (value.count > maxCount) {
          maxCount = value.count
          mostCommon = key
        }
      })

      data.summary.most_common_category = mostCommon
    }

    return data
  } catch (error) {
    console.error('Error fetching technical stats:', error)
    throw error
  }
}
